import { Injectable } from '@angular/core'
import { BehaviorSubject, Subject } from 'rxjs'
import { environment } from 'src/environments/environment.prod'
import { Application } from 'src/app/account/services/application.service'
import { IdentityProviderDto } from 'src/app/services/dtos'
import { Table } from 'primeng/table'
import { LoggerService } from '../account/services/logger.service'
import { ErrorAction, ErrorContext, PresentedError, toPresentedError } from 'src/utils/presented-error'
import { ErrorAnchorService } from './error-anchor.service'

declare global {
  interface Window {
    HubSpotConversations: {
      clear: ({ resetWidget }) => void
      widget: {
        open: () => void
      }
    }
  }
}

export interface ErrorMessage {
  message: string
  detailedMessage: string
  // Optional severity discriminator — defaults to 'error' (existing behavior).
  // 'success' renders as a short-lived green confirmation pill via the same shell toast.
  severity?: 'error' | 'success'
  // When true, the toast won't auto-dismiss; the user must click its close button.
  // Used for actionable backend error details we don't want to flash and lose.
  sticky?: boolean
}

/**
 * One entry in the shell's toast stack. Errors carry a `PresentedError` so the shell renders
 * them through `<arp-error-block>`; the success pill stays a plain string.
 */
export type ShellToast =
  | { kind: 'success'; message: string }
  // `onAction` is how a remedy survives the fall-through to a toast: the shell cannot call a
  // component method and a PresentedError carries data only, so the call site sends the closure
  // along with the failure. It dies with the toast.
  | { kind: 'error'; presented: PresentedError; onAction?: (action: ErrorAction) => void }

/** Everything `present` needs beyond the error itself. */
export interface PresentOptions {
  logMessage?: string
  /** Surfaces the call site named, innermost first. */
  ownerIds?: (string | undefined)[]
  onAction?: (action: ErrorAction) => void
}

@Injectable({
  providedIn: 'root',
})
export class UiService {
  /**
   * This service provides general-purpose "shell" functionality needed by various UI
   * components, such as the ability to announce an error message that the shell can
   * display as appropriate.
   */

  constructor(
    private logger: LoggerService,
    private anchors: ErrorAnchorService
  ) {}

  get productName() {
    return environment.productName
  }

  get termsOfServiceUrl() {
    return environment.termsUrl
  }

  get documentationUrl() {
    return 'https://docs.arpio.io'
  }

  get contactUrl() {
    return 'https://arpio.io/contact-us/'
  }

  get cloudFormationInstructionsUrl() {
    return 'https://docs.aws.amazon.com/AWSCloudFormation/latest/UserGuide/cfn-console-create-stack.html'
  }

  get generateKeyPairInstructionsUrl() {
    return 'https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/ec2-key-pairs.html#having-ec2-create-your-key-pair'
  }

  get importKeyPairInstructionsUrl() {
    return 'https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/ec2-key-pairs.html#how-to-generate-your-own-key-and-import-it-to-aws'
  }

  get s3BucketReplicationDocs() {
    return 'https://docs.aws.amazon.com/AmazonS3/latest/userguide/replication.html'
  }

  get failbackDocsUrl() {
    return 'https://docs.arpio.io/failback'
  }

  get recoveryDocsUrl() {
    return 'https://docs.arpio.io/performing-a-recovery'
  }

  get resourceSelectionDocsUrl() {
    return 'https://docs.arpio.io/creating-an-application#resources'
  }

  get testVsRecoveryFaq() {
    return 'https://docs.arpio.io/arpio-test-vs-recovery-faq'
  }

  get rtReplicationDocsUrl() {
    return 'https://docs.arpio.io/real-time-rpo-replication'
  }

  get networkSandboxDocsUrl() {
    return 'https://docs.arpio.io/network-sandbox'
  }

  get azureNetworkSandboxDocsUrl() {
    return 'https://docs.arpio.io/network-sandbox'
  }

  get ransomwareDocsUrl() {
    return 'https://docs.arpio.io/ransomware-recovery'
  }

  get s3BucketSuffix() {
    return 'https://docs.arpio.io/s3#bucket-suffix'
  }

  get retentionDocsUrl() {
    return 'https://docs.arpio.io/retention-policy'
  }

  get rbacDocsUrl() {
    return 'https://docs.arpio.io/role-based-access-controls-rbac'
  }

  get billableResourcesDocsUrl() {
    return 'https://docs.arpio.io/what-resources-count-against-my-arpio-subscription'
  }

  get s3BucketNamingRules() {
    return 'https://docs.aws.amazon.com/AmazonS3/latest/userguide/bucketnamingrules.html'
  }

  get supportContact() {
    return 'support@arpio.io'
  }

  get awsMarketplaceErrorUrl() {
    return '/aws_marketplace/error'
  }

  get azureMarketplaceErrorUrl() {
    return '/azure_marketplace/error'
  }

  get bucketReplicationInstructionsUrl() {
    return 'https://docs.aws.amazon.com/AmazonS3/latest/userguide/replication-example-walkthroughs.html'
  }

  get azureBlobReplicationUrl() {
    return 'https://learn.microsoft.com/en-us/azure/storage/blobs/object-replication-overview'
  }

  // Components publish errors here; the shell renders them. A toast now means "no owning
  // surface was on screen" — anchored surfaces render the block themselves.
  private toastSource = new Subject<ShellToast>()
  public toastStream = this.toastSource.asObservable()

  // setup a facility for announcing new applications when they get created
  private applicationSource = new Subject<Application>()
  public applicationStream = this.applicationSource.asObservable()

  public bucketNameRegex: RegExp =
    /(?=^.{3,63}$)(?!^xn--)(?!.*-s3alias$)(?!^(\d{1,3}\.){3}\d{1,3}$)(^[a-z0-9]([a-z0-9-]*(\.[a-z0-9])?)*$)/

  // the public interface to publish a brief "Copied" success toast — reuses
  // the error toast pipeline so there's one shell-managed visual element
  // instead of parallel toast systems.
  announceCopied(message: string = 'Copied to clipboard') {
    this.toastSource.next({ kind: 'success', message })
  }

  // the public interface to announce errors.
  announceError(error: ErrorMessage) {
    if (error.severity === 'success') {
      this.announceCopied(error.message)
      return
    }

    this.present(toPresentedError(error, {}), { logMessage: error.message || error.detailedMessage })
  }

  /**
   * Handle and display API errors in a user-friendly way. All severity, tier, glyph, action
   * and reference decisions live in `toPresentedError`; this method only publishes the result.
   *
   * Pass `context.dialogId` and/or `context.objectId` from a surface that can render the failure
   * itself, and only from the surface the user is actually waiting on. Without one the error
   * tiers to a toast, which is the right answer for a background refresh whether or not a
   * dialog happens to be open.
   */
  announceApiError(error: any, fallbackMessage = 'An unexpected error occurred', context: ErrorContext = {}) {
    // Ids whose surface is not on screen are dropped here, so a matcher reading `dialogId` or
    // `objectId` can trust that surface is mounted and will be offered the error.
    const dialogId = this.anchors.ownsSurface(context.dialogId) ? context.dialogId : undefined
    const objectId = this.anchors.ownsSurface(context.objectId) ? context.objectId : undefined
    const { onAction, ...routing } = context
    const scoped = { ...routing, dialogId, objectId, anchored: Boolean(dialogId || objectId) }
    this.present(toPresentedError(error, scoped, fallbackMessage), {
      logMessage: fallbackMessage,
      ownerIds: [dialogId, objectId],
      onAction,
    })
  }

  /**
   * Publishes an already-mapped error. Offers it to the surface that owns it and only falls
   * back to the shell toast when nothing on screen claims it.
   */
  present(presented: PresentedError, options: PresentOptions = {}) {
    const { logMessage, ownerIds = [], onAction } = options
    // `announceApiError` defaults `logMessage`, so the mapped title is what distinguishes one
    // failure from another. `raw` rides along because an anchored error never reaches the
    // shell's console.error, which is the only other place the backend string is logged.
    this.logger.error(presented.title, { detail: presented.detail, raw: presented.reference.raw, logMessage })

    // Anchor before you announce: a toast means no surface on screen owned the failure.
    if (this.anchors.route(presented, ...ownerIds)) return

    this.toastSource.next({ kind: 'error', presented, onAction })
  }

  // the public interface to announce applications
  announceApplication(application: Application) {
    this.applicationSource.next(application)
  }

  openChat() {
    if (window.HubSpotConversations) {
      window.HubSpotConversations.clear({ resetWidget: true })
      window.HubSpotConversations.widget.open()
    } else {
      this.announceError({
        message: 'Unable to find HubSpot chat widget.',
      } as ErrorMessage)
    }
  }

  buildRootUrl() {
    return '/'
  }

  buildNewApplicationUrl(accountId: string) {
    return `/${accountId}/applications/new`
  }

  buildNewApplicationAccessMethodUrl(accountId: string) {
    return `/${accountId}/applications/new/access-method`
  }

  buildNewApplicationResourcesUrl(accountId: string) {
    return `/${accountId}/applications/new/resources`
  }

  buildNewApplicationConnectionUrl(accountId: string) {
    return `/${accountId}/applications/new/connect`
  }

  buildNewApplicationTemplateChoiceUrl(accountId: string) {
    return `/${accountId}/applications/new/templates`
  }

  buildApplicationUrl(accountId: string, applicationId: string) {
    return `/${accountId}/applications/${applicationId}`
  }

  buildCreateAccountUrl() {
    return '/create_account'
  }

  buildAccountUsersUrl(accountId: string) {
    return `/${accountId}/account/access-management`
  }

  buildUsageBillingUrl(accountId: string) {
    return `/${accountId}/account/usage`
  }

  buildRetentionPolicyUrl(accountId: string) {
    return `/${accountId}/account/settings/retention`
  }

  buildResourceConfigsUrl(accountId: string) {
    return `/${accountId}/account/settings/resources`
  }

  buildIntegrationsUrl(accountId: string) {
    return `/${accountId}/account/addons`
  }

  buildAccessManagementUrl(accountId: string) {
    return `/${accountId}/account/access-management`
  }

  buildUserManagementUrl(accountId: string) {
    return `/${accountId}/account/access-management`
  }

  buildRoleManagementUrl(accountId: string) {
    return `/${accountId}/account/access-management/roles`
  }

  buildGroupManagementUrl(accountId: string) {
    return `/${accountId}/account/access-management/groups`
  }

  buildApiKeyManagementUrl(accountId: string) {
    return `/${accountId}/account/settings/api-keys`
  }

  buildProfileUrl(accountId: string) {
    return `/${accountId}/profile`
  }

  buildAccountSupportUrl(accountId: string) {
    return `/${accountId}/support`
  }

  buildAccountResourceTagsUrl(accountId: string) {
    return `/${accountId}/account/settings/tags`
  }

  buildFinalizeInviteUrl() {
    // This URL should be absolute, because it gets passed into the invite
    // flow, which serves it back to the browser in a redirect response, possibly
    // from any host on the  internet.  In the native flow the redirect response
    // is handled by the UI.  For a SAML auth flow it's returned in response to
    // the POST to the ACS URL (and so it must be absolute to get us back here).
    return new URL('/auth/finishInvite', window.location.origin).toString()
  }

  buildAccountRootUrl(accountId: string) {
    return `/${accountId}`
  }

  buildAccountWelcomeUrl(accountId: string) {
    return `/${accountId}/welcome`
  }

  buildLoginUrl() {
    return '/auth/login'
  }

  buildResetPasswordUrl() {
    return '/auth/startResetPassword'
  }

  buildSyncPairUrl(
    accountId: string,
    sourceAwsAccountId: string,
    sourceRegion: string,
    targetAwsAccountId: string,
    targetRegion: string
  ) {
    return `/${accountId}/syncpairs/${sourceAwsAccountId}/${sourceRegion}/` + `${targetAwsAccountId}/${targetRegion}`
  }

  buildLinkAwsMarketplaceToAccountUrl(token: string = null) {
    if (!!token) {
      return `/aws_marketplace/link_account?awsToken=${token}`
    }
    return '/aws_marketplace/link_account'
  }

  buildLinkAzureMarketplaceToAccountUrl(token: string = null) {
    if (!!token) {
      return `/azure_marketplace/link_account?azureToken=${token}`
    }
    return '/azure_marketplace/link_account'
  }

  buildAccountSettingsUrl(accountId: string) {
    return `/${accountId}/account/settings`
  }

  buildAbsoluteAccountSettingsUrl(accountId: string) {
    return new URL(`/${accountId}/account/settings`, window.location.origin).toString()
  }

  buildSubscriptionSettingsUrl(accountId: string) {
    // for now, subscriptions are viewed on the account settings page
    return this.buildAccountSettingsUrl(accountId)
  }

  buildAccountPageUrl(accountId: string) {
    return `/${accountId}/account`
  }

  buildIdentityProviderName(idp: IdentityProviderDto): string {
    if (idp.type.toLowerCase() === 'native') {
      return idp.name
    } else {
      return `${idp.name} (${idp.type.toUpperCase()})`
    }
  }

  get drsAgentInstallationInstructionsUrl() {
    return 'https://docs.aws.amazon.com/drs/latest/userguide/agent-installation.html'
  }

  isCustomComponent$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  /**
   * Sorts a PrimeNG Table based on specified field and order.
   * This function ensures that the sorting operation is deferred until the table is fully initialized,
   * which is particularly useful when called from lifecycle hooks like ngAfterViewInit.
   *
   * @param {Table} table - The PrimeNG Table instance to sort.
   * @param {string} sortField - The field name on which to sort the table.
   * @param {number} sortOrder - The order of sorting: 1 for ascending, -1 for descending.
   */
  sortTable(table: Table, sortField: string, sortOrder: number): void {
    if (table) {
      setTimeout(() => {
        table.sortField = sortField
        table.sortOrder = sortOrder // 1 for ascending, -1 for descending
        table.sortSingle() // Perform the sort operation
      })
    }
  }
}
